import type { Delivery, DeliveryDocument, ServiceKey } from '@secretario/shared';

/**
 * Exemplos de entrega, para quem ainda não tem o Completo ver o que recebe antes
 * de assinar. Dados fictícios, marcados como exemplo na tela.
 */
const AT = '2026-10-01T12:00:00.000Z';

function sample(
  id: string,
  service: ServiceKey,
  title: string,
  subtitle: string,
  sections: DeliveryDocument['sections'],
  note: string | null,
  cover: DeliveryDocument['cover'] = null,
): Delivery {
  return {
    id: `00000000-0000-4000-8000-0000000000${id}`,
    service,
    title,
    subtitle,
    createdAt: AT,
    document: { title, subtitle, cover, sections, note },
    actions: [],
    shareText: null,
  };
}

export const DELIVERY_SAMPLES: Record<ServiceKey, Delivery> = {
  raio_x: {
    id: '00000000-0000-4000-8000-0000000000a1',
    service: 'raio_x',
    title: 'Raio-X do mês',
    subtitle: 'Outubro de 2026, a partir das contas que eu acompanho',
    createdAt: AT,
    document: {
      title: 'Raio-X do mês',
      subtitle: 'Outubro de 2026, a partir das contas que eu acompanho',
      cover: { value: 'R$ 3.412,40', label: 'no mês, R$ 1.432,40 ainda a vencer', tone: 'neutral' },
      sections: [
        {
          heading: null,
          blocks: [
            {
              type: 'facts',
              items: [
                { label: 'Contas do mês', value: 'R$ 3.412,40', tone: 'neutral' },
                { label: 'Já pago', value: 'R$ 1.980,00', tone: 'good' },
                { label: 'Ainda vence', value: 'R$ 1.432,40', tone: 'warn' },
                { label: 'Fixas por mês', value: 'R$ 2.646,80', tone: 'neutral' },
              ],
            },
          ],
        },
        {
          heading: 'Vencem nos próximos 15 dias',
          blocks: [
            {
              type: 'timeline',
              items: [
                {
                  when: 'sex, 09/10',
                  title: 'Condomínio',
                  detail: 'R$ 780,00 · todo mês',
                  tone: 'warn',
                },
                { when: 'seg, 12/10', title: 'Cartão', detail: 'R$ 512,50', tone: 'neutral' },
                {
                  when: 'qui, 15/10',
                  title: 'Internet',
                  detail: 'R$ 139,90 · todo mês',
                  tone: 'neutral',
                },
              ],
            },
          ],
        },
        {
          heading: 'Onde o mês pesa',
          blocks: [
            {
              type: 'bars',
              items: [
                { label: 'Aluguel', value: 1500, display: 'R$ 1.500,00', tone: 'neutral' },
                { label: 'Condomínio', value: 780, display: 'R$ 780,00', tone: 'neutral' },
                { label: 'Cartão', value: 512.5, display: 'R$ 512,50', tone: 'neutral' },
              ],
            },
          ],
        },
        {
          heading: 'Pontos de atenção',
          blocks: [
            {
              type: 'checklist',
              items: [
                {
                  when: null,
                  text: 'Condomínio e cartão caem na mesma semana: separe os dois valores já.',
                },
                {
                  when: null,
                  text: 'As fixas são 78% do mês. Revisar o plano de internet alivia o ano.',
                },
                { when: null, text: 'Nada atrasado. Mantendo assim, o mês fecha sem juros.' },
              ],
            },
          ],
        },
      ],
      note: 'Organização das suas contas. Não é recomendação de investimento.',
    },
    actions: [],
    shareText: null,
  },
  assinaturas: {
    id: '00000000-0000-4000-8000-0000000000a2',
    service: 'assinaturas',
    title: 'Caça-assinaturas',
    subtitle: '7 cobranças fixas, R$ 3.946,80 por ano',
    createdAt: AT,
    document: {
      title: 'Caça-assinaturas',
      subtitle: '7 cobranças fixas, R$ 3.946,80 por ano',
      cover: { value: 'R$ 3.946,80', label: 'por ano em cobranças que se repetem', tone: 'warn' },
      sections: [
        {
          heading: null,
          blocks: [
            {
              type: 'facts',
              items: [
                { label: 'Cobranças que se repetem', value: '7', tone: 'neutral' },
                { label: 'Por mês', value: 'R$ 328,90', tone: 'neutral' },
                { label: 'No ano', value: 'R$ 3.946,80', tone: 'warn' },
              ],
            },
          ],
        },
        {
          heading: 'Quanto cada uma custa no ano',
          blocks: [
            {
              type: 'bars',
              items: [
                { label: 'Canal de esportes', value: 718.8, display: 'R$ 718,80', tone: 'neutral' },
                {
                  label: 'Streaming de filmes',
                  value: 538.8,
                  display: 'R$ 538,80',
                  tone: 'neutral',
                },
                {
                  label: 'Streaming de séries',
                  value: 478.8,
                  display: 'R$ 478,80',
                  tone: 'neutral',
                },
              ],
            },
          ],
        },
        {
          heading: 'Streaming e TV',
          blocks: [
            {
              type: 'list',
              items: [
                { label: 'Streaming de filmes', value: 'R$ 44,90/mês', note: 'R$ 538,80 no ano' },
                {
                  label: 'Streaming de séries',
                  value: 'R$ 39,90/mês',
                  note: 'R$ 478,80 no ano · ver se ainda usa',
                },
                {
                  label: 'Canal de esportes',
                  value: 'R$ 59,90/mês',
                  note: 'R$ 718,80 no ano · só na temporada?',
                },
              ],
            },
          ],
        },
        {
          heading: 'Parecidas entre si',
          blocks: [
            {
              type: 'text',
              paragraphs: [
                'Dois serviços de streaming com catálogos parecidos somam R$ 1.017,60 por ano.',
              ],
            },
          ],
        },
      ],
      note: 'Valores das contas que eu acompanho e do que você colou. Confira na fatura antes de cancelar.',
    },
    actions: [],
    shareText: null,
  },
  carta: {
    id: '00000000-0000-4000-8000-0000000000a3',
    service: 'carta',
    title: 'Pedido 48213 não entregue',
    subtitle: 'Pedir o dinheiro de volta · Loja Exemplo',
    createdAt: AT,
    document: {
      title: 'Pedido 48213 não entregue',
      subtitle: 'Pedir o dinheiro de volta · Loja Exemplo',
      cover: null,
      sections: [
        {
          heading: null,
          blocks: [
            {
              type: 'letter',
              to: 'À Loja Exemplo, atendimento ao cliente',
              subject: 'Pedido 48213 não entregue',
              paragraphs: [
                'Comprei uma air fryer no dia 10/09/2026, pedido 48213, com entrega prometida para 20/09/2026. Até hoje o produto não chegou e o rastreio não mostra movimento desde 14/09.',
                'Já procurei o atendimento em 22/09 (protocolo [número do protocolo]) e não tive retorno.',
                'Peço a devolução integral do valor pago, R$ 389,90, em até 5 dias úteis, conforme o Código de Defesa do Consumidor.',
              ],
              closing: 'Atenciosamente,\nAna Souza\n[seu telefone ou e-mail]',
            },
          ],
        },
        {
          heading: 'O que juntar',
          blocks: [
            {
              type: 'checklist',
              items: [
                { when: null, text: 'Comprovante de pagamento' },
                { when: null, text: 'Número do pedido' },
                { when: null, text: 'Prints da oferta ou do anúncio' },
              ],
            },
          ],
        },
      ],
      note: 'Modelo para você conferir, completar e enviar. Não é orientação jurídica sobre o seu caso.',
    },
    actions: [],
    shareText: null,
  },
  viagem: {
    id: '00000000-0000-4000-8000-0000000000a4',
    service: 'viagem',
    title: 'Roteiro: Ouro Preto',
    subtitle: 'sex, 13/11 a dom, 15/11 · 3 dias · 2 pessoas',
    createdAt: AT,
    document: {
      title: 'Roteiro: Ouro Preto',
      subtitle: 'sex, 13/11 a dom, 15/11 · 3 dias · 2 pessoas',
      cover: {
        value: '3 dias',
        label: 'em Ouro Preto, de sex, 13/11 a dom, 15/11',
        tone: 'neutral',
      },
      sections: [
        {
          heading: 'Dia 1 · sex, 13/11',
          blocks: [
            {
              type: 'timeline',
              items: [
                {
                  when: 'Manhã',
                  title: 'Chegada e check-in. Café sem pressa perto da Praça Tiradentes.',
                  detail: null,
                  tone: 'neutral',
                },
                {
                  when: 'Tarde',
                  title: 'Igreja de São Francisco de Assis e as ladeiras do centro histórico.',
                  detail: null,
                  tone: 'neutral',
                },
                {
                  when: 'Noite',
                  title: 'Jantar de comida mineira e volta a pé pela praça iluminada.',
                  detail: null,
                  tone: 'neutral',
                },
              ],
            },
          ],
        },
        {
          heading: 'Documentos e o que levar',
          blocks: [
            {
              type: 'checklist',
              items: [
                { when: null, text: 'Documento com foto' },
                { when: null, text: 'Tênis confortável: as ruas são de pedra e em subida' },
                { when: null, text: 'Casaco leve para a noite' },
              ],
            },
          ],
        },
      ],
      note: 'Confira horários, preços e regras de entrada nos sites oficiais antes de viajar.',
    },
    actions: [],
    shareText: null,
  },
  festa: {
    id: '00000000-0000-4000-8000-0000000000a5',
    service: 'festa',
    title: 'Aniversário de 40 anos',
    subtitle: 'sáb, 21/11 · em casa',
    createdAt: AT,
    document: {
      title: 'Aniversário de 40 anos',
      subtitle: 'sáb, 21/11 · em casa',
      cover: { value: '30 convidados', label: 'em sáb, 21/11', tone: 'neutral' },
      sections: [
        {
          heading: null,
          blocks: [
            {
              type: 'facts',
              items: [
                { label: 'Quando', value: 'sáb, 21/11', tone: 'neutral' },
                { label: 'Convidados', value: '30', tone: 'neutral' },
              ],
            },
          ],
        },
        {
          heading: 'Até o dia',
          blocks: [
            {
              type: 'checklist',
              items: [
                { when: 'sáb, 31/10', text: 'Fechar a lista de convidados e mandar o convite' },
                { when: 'sáb, 14/11', text: 'Encomendar o bolo e os salgados' },
                { when: 'qui, 19/11', text: 'Comprar bebidas e descartáveis' },
                { when: 'No dia', text: 'Montar a mesa e gelar as bebidas pela manhã' },
              ],
            },
          ],
        },
        {
          heading: 'Convite',
          blocks: [
            {
              type: 'text',
              paragraphs: [
                'Chegaram os 40! Quero comemorar com você no sábado, 21/11, às 19h, aqui em casa. Confirma até dia 14?',
              ],
            },
          ],
        },
      ],
      note: 'Quantidades são uma estimativa para o número de convidados. Ajuste ao seu gosto.',
    },
    actions: [],
    shareText: null,
  },
  dossie: {
    id: '00000000-0000-4000-8000-0000000000a6',
    service: 'dossie',
    title: 'Dossiê: reforma tributária',
    subtitle: '5 fontes públicas, com palavras nossas',
    createdAt: AT,
    document: {
      title: 'Dossiê: reforma tributária',
      subtitle: '5 fontes públicas, com palavras nossas',
      cover: null,
      sections: [
        {
          heading: 'O que se sabe',
          blocks: [
            {
              type: 'text',
              paragraphs: [
                'A regulamentação avançou no Congresso e define como os novos impostos sobre consumo começam a valer, com uma transição de alguns anos.',
              ],
            },
          ],
        },
        {
          heading: 'Pontos principais',
          blocks: [
            {
              type: 'cards',
              items: [
                {
                  kicker: 'Ponto 1',
                  title: 'Dois novos tributos',
                  text: 'Os impostos atuais sobre consumo dão lugar a dois novos tributos.',
                },
                {
                  kicker: 'Ponto 2',
                  title: 'Troca aos poucos',
                  text: 'A troca acontece aos poucos, ao longo da transição.',
                },
                {
                  kicker: 'Ponto 3',
                  title: 'Cesta básica',
                  text: 'Alguns itens da cesta básica ficam com alíquota reduzida.',
                },
              ],
            },
          ],
        },
        {
          heading: 'Fontes',
          blocks: [
            {
              type: 'list',
              items: [
                {
                  label: 'Congresso conclui votação da regulamentação',
                  value: null,
                  note: 'g1',
                  url: null,
                },
                { label: 'O que muda para o consumidor', value: null, note: 'Folha', url: null },
              ],
            },
          ],
        },
      ],
      note: 'Resumo com palavras nossas a partir de notícias públicas. Leia a íntegra no veículo.',
    },
    actions: [],
    shareText: null,
  },
  semana: {
    id: '00000000-0000-4000-8000-0000000000a7',
    service: 'semana',
    title: 'Resumo da semana',
    subtitle: 'Até sex, 02/10 · 6 destaques dos seus assuntos',
    createdAt: AT,
    document: {
      title: 'Resumo da semana',
      subtitle: 'Até sex, 02/10 · 6 destaques dos seus assuntos',
      cover: null,
      sections: [
        {
          heading: 'Dinheiro',
          blocks: [
            {
              type: 'list',
              items: [
                {
                  label: 'Juros param de subir',
                  value: null,
                  note: 'O Banco Central manteve a taxa e indicou cautela nos próximos meses. · g1',
                  url: null,
                },
                {
                  label: 'Gasolina fica mais barata nas bombas',
                  value: null,
                  note: 'A redução chegou aos postos depois do corte nas refinarias. · Valor Investe',
                  url: null,
                },
              ],
            },
          ],
        },
        {
          heading: 'Tecnologia',
          blocks: [
            {
              type: 'list',
              items: [
                {
                  label: 'Novo golpe usa mensagem de entrega',
                  value: null,
                  note: 'Mensagens falsas pedem pagamento de taxa para liberar encomenda. · Canaltech',
                  url: null,
                },
              ],
            },
          ],
        },
      ],
      note: 'Resumos escritos por nós a partir de notícias públicas. Leia a íntegra no veículo.',
    },
    actions: [],
    shareText: null,
  },
  irpf: sample(
    'a8',
    'irpf',
    'Prévia do Imposto de Renda',
    'Com os números de exemplo · tende a compensar o completo',
    [
      {
        heading: null,
        blocks: [
          {
            type: 'facts',
            items: [
              { label: 'Rendimentos tributáveis', value: 'R$ 96.000,00', tone: 'neutral' },
              { label: 'Imposto já retido', value: 'R$ 11.200,00', tone: 'neutral' },
              { label: 'Deduções no completo', value: 'R$ 21.540,16', tone: 'neutral' },
              { label: 'Desconto no simplificado', value: 'R$ 17.640,00', tone: 'neutral' },
            ],
          },
          {
            type: 'compare',
            options: [
              {
                title: 'Completo',
                value: 'R$ 21.540,16',
                lines: ['Soma as deduções com comprovante', 'Saúde sem limite'],
                best: true,
              },
              {
                title: 'Simplificado',
                value: 'R$ 17.640,00',
                lines: ['20% dos rendimentos', 'Até R$ 17.640,00'],
                best: false,
              },
            ],
          },
          {
            type: 'text',
            paragraphs: [
              'O modelo completo tende a compensar: abate R$ 3.900,16 a mais da base do imposto. A escolha final aparece no programa da Receita.',
            ],
          },
        ],
      },
      {
        heading: 'Documentos para separar',
        blocks: [
          {
            type: 'checklist',
            items: [
              { when: null, text: 'Informe de rendimentos de cada empresa que pagou você' },
              { when: null, text: 'Recibos de saúde com CPF ou CNPJ de quem atendeu' },
              { when: null, text: 'CPF e data de nascimento de cada dependente' },
            ],
          },
        ],
      },
    ],
    'Prévia para você conferir. Não é a declaração e não substitui a Receita nem um contador.',
    { value: 'Completo', label: 'tende a compensar, abatendo R$ 3.900,16 a mais', tone: 'good' },
  ),
  mei: sample(
    'a9',
    'mei',
    'MEI em dia',
    'Outubro de 2026 · 74% do limite do ano usado',
    [
      {
        heading: null,
        blocks: [
          {
            type: 'facts',
            items: [
              { label: 'Faturado no ano', value: 'R$ 60.000,00', tone: 'neutral' },
              { label: 'Limite do ano', value: 'R$ 81.000,00', tone: 'neutral' },
              { label: 'Usado do limite', value: '74%', tone: 'good' },
              { label: 'Projeção até dezembro', value: 'R$ 72.000,00', tone: 'neutral' },
            ],
          },
          {
            type: 'meter',
            label: 'Limite do ano',
            value: 0.74,
            display: '74%',
            caption: 'No ritmo atual, R$ 72.000,00 até dezembro',
            marks: [
              { at: 0.8, label: '80%' },
              { at: 0.89, label: 'Dezembro' },
            ],
            tone: 'good',
          },
        ],
      },
      {
        heading: 'Todo mês',
        blocks: [
          {
            type: 'checklist',
            items: [
              { when: 'Até o dia 20', text: 'Pagar o DAS do mês, mesmo sem faturamento.' },
              { when: null, text: 'Anotar o faturamento do mês, com e sem nota.' },
            ],
          },
        ],
      },
    ],
    'Para dúvidas do seu caso, fale com um contador.',
    { value: '74%', label: 'do limite do ano usado (R$ 60.000,00 de R$ 81.000,00)', tone: 'good' },
  ),
  carreira: sample(
    'aa',
    'carreira',
    'Currículo: analista de marketing',
    'Bruna Lima · uma página, pronto para enviar',
    [
      {
        heading: 'Resumo',
        blocks: [
          {
            type: 'text',
            paragraphs: [
              'Profissional de marketing com 4 anos em redes sociais e campanhas para o varejo, focada em resultado e em conteúdo que vende.',
            ],
          },
        ],
      },
      {
        heading: 'Treino de entrevista',
        blocks: [
          {
            type: 'checklist',
            items: [
              {
                when: 'Fale de uma campanha que deu certo',
                text: 'Conte a campanha de Dia das Mães: o objetivo, o que você fez e o aumento de vendas.',
              },
            ],
          },
        ],
      },
    ],
    'Use só o que é verdade sobre você.',
  ),
  estudos: sample(
    'ab',
    'estudos',
    'Plano de estudos: inglês para viagem',
    'Até qua, 16/12 · 4 h por semana · 10 semanas',
    [
      {
        heading: 'Semana a semana',
        blocks: [
          {
            type: 'checklist',
            items: [
              {
                when: 'Semana 1 · seg, 05/10',
                text: 'Aeroporto e hotel. Vocabulário de chegada e 20 frases prontas',
              },
              {
                when: 'Semana 2 · seg, 12/10',
                text: 'Restaurantes. Pedir, perguntar e pagar; revisão da semana 1',
              },
            ],
          },
        ],
      },
    ],
    null,
    { value: '40 h', label: 'de estudo em 10 semanas, até qua, 16/12', tone: 'neutral' },
  ),
  segunda: sample(
    'ac',
    'segunda',
    'Reunião de segunda',
    'Semana de seg, 05/10 a dom, 11/10',
    [
      {
        heading: null,
        blocks: [
          {
            type: 'facts',
            items: [
              { label: 'Compromissos', value: '4', tone: 'neutral' },
              { label: 'Contas da semana', value: 'R$ 1.292,50', tone: 'warn' },
              { label: 'Datas importantes', value: '1', tone: 'neutral' },
              { label: 'Itens na lista', value: '8', tone: 'neutral' },
            ],
          },
        ],
      },
      {
        heading: 'Prioridades da semana',
        blocks: [
          {
            type: 'checklist',
            items: [
              { when: null, text: 'Pagar condomínio e cartão até quarta.' },
              { when: null, text: 'Comprar o presente da Carol antes de sábado.' },
              { when: null, text: 'Confirmar o dentista de quinta.' },
            ],
          },
        ],
      },
      {
        heading: 'Na agenda',
        blocks: [
          {
            type: 'timeline',
            items: [
              { when: 'seg, 9h', title: 'Reunião com o time', detail: null, tone: 'neutral' },
              { when: 'qua', title: 'Vence o condomínio', detail: 'R$ 780,00', tone: 'warn' },
              { when: 'qui, 15h', title: 'Dentista', detail: null, tone: 'neutral' },
              { when: 'sáb', title: 'Aniversário da Carol', detail: null, tone: 'good' },
            ],
          },
        ],
      },
    ],
    null,
    { value: '4', label: 'compromissos na semana', tone: 'neutral' },
  ),
  fecho: sample(
    'ad',
    'fecho',
    'Fecho do mês',
    'Setembro de 2026',
    [
      {
        heading: null,
        blocks: [
          {
            type: 'facts',
            items: [
              { label: 'Contas pagas', value: 'R$ 3.104,30', tone: 'good' },
              { label: 'Para o mês que vem', value: 'R$ 2.646,80', tone: 'warn' },
              { label: 'Compromissos', value: '12', tone: 'neutral' },
              { label: 'Reuniões e entregas', value: '3', tone: 'neutral' },
            ],
          },
        ],
      },
      {
        heading: 'Para onde foi',
        blocks: [
          {
            type: 'bars',
            items: [
              { label: 'Aluguel', value: 1500, display: 'R$ 1.500,00', tone: 'neutral' },
              { label: 'Condomínio', value: 780, display: 'R$ 780,00', tone: 'neutral' },
              { label: 'Cartão', value: 512.5, display: 'R$ 512,50', tone: 'neutral' },
              { label: 'Luz', value: 171.9, display: 'R$ 171,90', tone: 'neutral' },
            ],
          },
        ],
      },
      {
        heading: 'Destaques',
        blocks: [
          {
            type: 'checklist',
            items: [
              { when: null, text: 'Nenhuma conta atrasada no mês.' },
              { when: null, text: 'Roteiro de Ouro Preto pronto e na agenda.' },
            ],
          },
        ],
      },
    ],
    null,
    { value: 'R$ 3.104,30', label: 'em contas pagas no mês', tone: 'good' },
  ),
};
